import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, Inject, Injectable, signal } from '@angular/core';
import { catchError, of, Subscription } from 'rxjs';
import { ApplicationId } from '../../../lib';
import { API_PLANNING_PROJECTS_CONFIG, ApiPlanningProjectsConfig } from '../api-plannig-projects.config';
import { ProductsUtils, GetProductsRequestPost, Product, ProductsResponseGetRecordsList, ProductType } from '../common';

@Injectable()
export class ProductsService {
    private inflight?: Subscription;
    private readonly endpoint = '/products';
    private readonly baseUrl: string;
    private readonly applicationId: ApplicationId = ApplicationId.ViGuidePlanning;

    private readonly countryCode = signal<GetProductsRequestPost['countryCode']>(ProductsUtils.defaultCountryCode);
    private readonly languageCode = signal<GetProductsRequestPost['languageCode']>(ProductsUtils.defaultLanguageCode);
    private readonly keyword = signal<string | undefined>(undefined);
    private readonly productType = signal<ProductType | undefined>(undefined);
    private readonly includeMarketingFormattedTexts = signal<boolean>(ProductsUtils.defaultIncludeMarketingFormattedTexts);
    private readonly pageSize = signal<number>(ProductsUtils.defaultPageSize);
    private readonly productIDs = signal<string[] | undefined>(undefined);

    readonly pageIndex = signal<number>(0);
    readonly products = signal<Product[]>([]);
    readonly loading = signal<boolean>(false);
    readonly error = signal<boolean>(false);
    readonly hasLoadedOnce = signal<boolean>(false);
    readonly hasNextPage = computed(() => this.products().length === this.pageSize());
    readonly hasPreviousPage = computed(() => this.pageIndex() > 0);
    readonly totalPages = computed(() => this.pageIndex() + (this.hasNextPage() ? 2 : 1));

    constructor(@Inject(API_PLANNING_PROJECTS_CONFIG) config: ApiPlanningProjectsConfig, private httpClient: HttpClient) {
        this.baseUrl = config.apiBaseUrl;
        this.applicationId = config.applicationId;
    }

    setLocale(languageCode: GetProductsRequestPost['languageCode'], countryCode: GetProductsRequestPost['countryCode']) {
        this.languageCode.set(languageCode);
        this.countryCode.set(countryCode);
    }

    readProducts(partialOverrides?: Partial<GetProductsRequestPost>): void {
        const baseBuildRequest = this.buildRequest();
        const buildRequest: GetProductsRequestPost = {
            ...baseBuildRequest,
            ...partialOverrides
        };

        this.inflight?.unsubscribe();
        this.loading.set(true);
        this.error.set(false);

        this.inflight = this.fetchProducts(buildRequest)
            .pipe(
                catchError(() => {
                    this.error.set(true);
                    return of<ProductsResponseGetRecordsList>({ products: [] });
                })
            )
            .subscribe(res => {
                this.products.set(res.products ?? []);
                this.loading.set(false);
                this.hasLoadedOnce.set(true);
            });
    }

    cancelPending() {
        this.inflight?.unsubscribe();
        this.inflight = undefined;
        this.loading.set(false);
    }

    setCountryCode(code: GetProductsRequestPost['countryCode']) {
        this.countryCode.set(code);
        this.resetToFirstPage();
    }

    setLanguageCode(code: GetProductsRequestPost['languageCode']) {
        this.languageCode.set(code);
        this.resetToFirstPage();
    }

    setKeyword(value: string | undefined) {
        this.keyword.set(value?.trim() || undefined);
        this.resetToFirstPage();
    }

    setProductType(value: ProductType | undefined) {
        this.productType.set(value);
        this.resetToFirstPage();
    }

    setPageSize(size: number) {
        this.pageSize.set(size);
        this.pageIndex.set(0);
    }

    setPageIndex(index: number) {
        this.pageIndex.set(Math.max(0, index));
    }

    setProductIDs(ids: string[]) {
        this.productIDs.set(ids ?? []);
        this.resetToFirstPage();
    }

    previousPage(): void {
        if (!this.hasPreviousPage()) {
            return;
        }
        this.pageIndex.set(this.pageIndex() - 1);
        this.readProducts();
    }

    nextPage(): void {
        if (!this.hasNextPage()) {
            return;
        }
        this.pageIndex.set(this.pageIndex() + 1);
        this.readProducts();
    }

    private resetToFirstPage() {
        this.pageIndex.set(0);
    }

    private fetchProducts(body: GetProductsRequestPost) {
        const url = `${this.baseUrl}${this.endpoint}`;
        return this.httpClient.post<ProductsResponseGetRecordsList>(url, body, { headers: this.headers });
    }

    private readonly buildRequest = computed<GetProductsRequestPost>(() => ({
        countryCode: this.countryCode(),
        languageCode: this.languageCode(),
        keyword: this.keyword(),
        // TODO: reintroduce when backend supports filtering by product type
        // productType: this.productType(),
        pageIndex: this.pageIndex(),
        pageSize: this.pageSize(),
        includeMarketingFormattedTexts: this.includeMarketingFormattedTexts(),
        productIDs: this.productIDs()
    }));

    private get headers(): HttpHeaders {
        return new HttpHeaders()
            .set('X-APPLICATION-ID', this.applicationId)
            .set('X-APPLICATION-COUNTRY-CODE', this.countryCode())
            .set('X-CA-APPLICATION-LANG-CODE', this.languageCode());
    }
}
